// src/features/reports/components/Reports/hooks/useReportsData.js
// ✅ كل منطق الصفحة: state، fetch، فلاتر، قيم مشتقة، pagination

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import * as XLSX from 'xlsx';
import api from '../../../../../api/axiosInstance';
import { useAuth } from '../../../../auth/context/AuthContext';
import { formatNumber } from '../../../../../shared/utils/formatters';

// ─── ثوابت ───────────────────────────────────────────────────────
export const PAGE_SIZE = 20;

export const ALL_BOOKING_TYPES = [
  { value: 'graduation',        label: 'حفلات التخرج'  },
  { value: 'wedding',           label: 'الأعراس'        },
  { value: 'kids',              label: 'تصوير أطفال'    },
  { value: 'event',             label: 'مناسبات'         },
  { value: 'special',           label: 'طلب خاص'        },
  { value: 'digital_marketing', label: 'تسويق إلكتروني' },
];

export const TYPE_MAP = {
  graduation:        'تخرج',
  wedding:           'عرس',
  kids:              'أطفال',
  event:             'مناسبة',
  special:           'خاص',
  digital_marketing: 'تسويق',
};

export const STATUS_MAP = {
  completed:   { label: 'مكتمل',       cls: 'sCompleted'  },
  in_progress: { label: 'قيد التنفيذ', cls: 'sInProgress' },
  confirmed:   { label: 'مؤكد',        cls: 'sConfirmed'  },
  pending:     { label: 'انتظار',      cls: 'sPending'    },
  cancelled:   { label: 'ملغي',        cls: 'sCancelled'  },
};

// أول وآخر يوم في الشهر الحالي
const getMonthRange = () => {
  const now  = new Date();
  const y    = now.getFullYear();
  const m    = String(now.getMonth() + 1).padStart(2, '0');
  const last = new Date(y, now.getMonth() + 1, 0).getDate();
  return { start: `${y}-${m}-01`, end: `${y}-${m}-${last}` };
};

const MONTH = getMonthRange();

// ─── الـ Hook الرئيسي ─────────────────────────────────────────────
export default function useReportsData() {
  const { loggedInUser } = useAuth();
  const printableRef = useRef(null);

  // ── State ────────────────────────────────────────────────────────
  const [reportData, setReportData] = useState(null);
  const [settings,   setSettings]   = useState(null);
  const [loading,    setLoading]     = useState(true);
  const [error,      setError]       = useState('');

  const [filters, setFilters] = useState({
    start_date:   MONTH.start,
    end_date:     MONTH.end,
    status:       'all',
    booking_type: '',
  });

  // عرض: revenue | expenses
  const [tableView,     setTableView]     = useState('revenue');
  const [expenseFilter, setExpenseFilter] = useState('all');
  const [displayMode,   setDisplayMode]   = useState('table'); // table | cards

  // صفحات
  const [revPage, setRevPage] = useState(1);
  const [expPage, setExpPage] = useState(1);

  // ── الأنواع المسموحة ──────────────────────────────────────────────
  const allowedBookingTypes = useMemo(() => {
    if (loggedInUser?.is_admin || loggedInUser?.is_superuser) return ALL_BOOKING_TYPES;
    if (!loggedInUser?.departments?.length) return [];
    const ok = loggedInUser.departments.map(d => d.associated_booking_type);
    return ALL_BOOKING_TYPES.filter(t => ok.includes(t.value));
  }, [loggedInUser]);

  // ── جلب البيانات ──────────────────────────────────────────────────
  const generateReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [rRes, sRes] = await Promise.all([
        api.get('/financial-report/', { params: filters }),
        api.get('/settings/'),
      ]);
      setReportData(rRes.data);
      setSettings(sRes.data);
      setRevPage(1);
      setExpPage(1);
    } catch {
      setError('حدث خطأ أثناء جلب بيانات التقرير.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { generateReport(); }, [generateReport]);

  // ── الطباعة ───────────────────────────────────────────────────────
  const handlePrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Report_${filters.start_date}_${filters.end_date}`,
  });

  // ── التصدير لـ Excel ──────────────────────────────────────────────
  const handleExport = useCallback(() => {
    if (!reportData) return;
    const isSpec   = filters.booking_type !== '';
    const bExp     = parseFloat(reportData.summary.total_booking_expenses) || 0;
    const gExp     = parseFloat(reportData.summary.total_general_expenses) || 0;
    const totalExp = isSpec ? bExp : bExp + gExp;

    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['البيان', 'المبلغ'],
      ['إجمالي الإيرادات',   parseFloat(reportData.summary.total_revenue) || 0],
      ['إجمالي المصاريف',    totalExp],
      ['صافي الربح',         (parseFloat(reportData.summary.total_revenue) || 0) - totalExp],
      [],
      ['عدد الحجوزات',       reportData.summary.bookings_count],
      ['إجمالي المستلم',     parseFloat(reportData.summary.total_received) || 0],
      ['إجمالي المتبقي',     parseFloat(reportData.summary.total_remaining) || 0],
    ]), 'الملخص');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      reportData.bookings_details.map(b => ({
        'العميل':     b.client?.name,
        'نوع الحجز':  b.booking_type,
        'التاريخ':    b.booking_date,
        'الحالة':     b.status,
        'الإجمالي':   parseFloat(b.total_price) || 0,
        'المدفوع':    parseFloat(b.total_paid) || 0,
        'المصاريف':   parseFloat(b.total_booking_expenses) || 0,
        'المتبقي':    parseFloat(b.remaining_amount) || 0,
      }))
    ), 'الحجوزات');

    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      reportData.all_expenses_details.map(e => ({
        'الحساب':   e.category_name,
        'البيان':   e.description,
        'الحجز':    e.booking
          ? (reportData.bookings_details.find(b => b.id === e.booking)?.client?.name || 'حجز')
          : 'عام',
        'التاريخ':  e.expense_date,
        'المبلغ':   parseFloat(e.amount) || 0,
      }))
    ), 'المصاريف');

    XLSX.writeFile(wb, `Report_${filters.start_date}_${filters.end_date}.xlsx`);
  }, [reportData, filters]);

  // ── معالجة الفلاتر ───────────────────────────────────────────────
  const handleFilterChange = useCallback(e => {
    setFilters(p => ({ ...p, [e.target.name]: e.target.value }));
  }, []);

  // ── قيم مشتقة ────────────────────────────────────────────────────
  const isSpecFilter = filters.booking_type !== '';

  const { netProfit, totalExpenses } = useMemo(() => {
    const rev  = parseFloat(reportData?.summary?.total_revenue)           || 0;
    const bExp = parseFloat(reportData?.summary?.total_booking_expenses)  || 0;
    const gExp = parseFloat(reportData?.summary?.total_general_expenses)  || 0;
    const tot  = isSpecFilter ? bExp : bExp + gExp;
    return { netProfit: rev - tot, totalExpenses: tot };
  }, [reportData, isSpecFilter]);

  const genExpValue = isSpecFilter
    ? 0
    : (parseFloat(reportData?.summary?.total_general_expenses) || 0);

  // المصاريف المفلترة
  const filteredExpenses = useMemo(() => {
    if (!reportData?.all_expenses_details) return [];
    if (expenseFilter === 'booking') return reportData.all_expenses_details.filter(e => e.booking);
    if (expenseFilter === 'general') return reportData.all_expenses_details.filter(e => !e.booking);
    return reportData.all_expenses_details;
  }, [reportData, expenseFilter]);

  // بيانات الصفحة الحالية
  const revRows = useMemo(() => {
    const all = reportData?.bookings_details || [];
    return all.slice((revPage - 1) * PAGE_SIZE, revPage * PAGE_SIZE);
  }, [reportData, revPage]);

  const expRows = useMemo(() => {
    return filteredExpenses.slice((expPage - 1) * PAGE_SIZE, expPage * PAGE_SIZE);
  }, [filteredExpenses, expPage]);

  return {
    // data
    reportData, settings, loading, error,
    // filters
    filters, allowedBookingTypes,
    handleFilterChange, generateReport,
    // view state
    tableView, setTableView,
    expenseFilter, setExpenseFilter,
    displayMode, setDisplayMode,
    // pagination
    revPage, setRevPage, revRows,
    expPage, setExpPage, expRows,
    // derived
    netProfit, totalExpenses, genExpValue,
    filteredExpenses, isSpecFilter,
    // print / export
    printableRef, handlePrint, handleExport,
  };
}